import {
  Airline,
  Airport,
  AncillaryPostBookingMyTripsFetchOffersResponse,
  BookedFlightItineraryWithDepartureTime,
  FlightItineraryResponse,
  MyTripsFilter,
} from "redmond";
import { call, put, select } from "redux-saga/effects";
import { fetchFlights } from "../../../api/v1/itinerary/fetchFlights";
import { actions } from "../actions";
import {
  getAirlinesMap,
  getAirportMap,
  getFlights,
  getTripsFilter,
  shouldExcludeCorporateTripsSelector,
} from "../reducer";
import { getIsCorporateForFlight } from "../utils";
import { setUpMyTripsParams } from "./setUpTripsParams";
import getPostBookingMyTripsOffers from "../../../api/v1/disruptions/getPostBookingMyTripsOffers";

export function* fetchFlightsSaga(action: actions.IFetchFlights) {
  try {
    const { tripId } = yield call(setUpMyTripsParams, action);
    const flightItinerariesResponse: FlightItineraryResponse =
      yield fetchFlights(action.request);
    const tripsFilter: MyTripsFilter = yield select(getTripsFilter);
    const flightItineriaries: {
      [key: string]: BookedFlightItineraryWithDepartureTime[];
    } = yield select(getFlights);

    const {
      future: newFuture,
      canceled: newCanceled,
      past: newPast,
      present: newPresent,
    } = flightItinerariesResponse.itineraries;

    const future = getNewFlightItineraryArray(newFuture, [
      ...flightItineriaries.future,
    ]);
    const canceled = getNewFlightItineraryArray(newCanceled, [
      ...flightItineriaries.canceled,
    ]);
    const past = getNewFlightItineraryArray(newPast, [
      ...flightItineriaries.past,
    ]);
    const present = getNewFlightItineraryArray(newPresent, [
      ...flightItineriaries.present,
    ]);

    const airlines: { [key: string]: Airline } = yield select(getAirlinesMap);
    const responseAirlinesMapArr = Object.keys(
      flightItinerariesResponse.airlines
    );
    for (let i = 0; i < responseAirlinesMapArr.length; i++) {
      const currentAirline = responseAirlinesMapArr[i];
      if (!airlines[responseAirlinesMapArr[i]]) {
        airlines[currentAirline] =
          flightItinerariesResponse.airlines[currentAirline];
      }
    }

    const airports: { [key: string]: Airport } = yield select(getAirportMap);
    const responseAirportMapArr = Object.keys(
      flightItinerariesResponse.airports
    );
    for (let i = 0; i < responseAirportMapArr.length; i++) {
      if (!airlines[responseAirportMapArr[i]]) {
        const currentAirport = responseAirportMapArr[i];
        airports[currentAirport] =
          flightItinerariesResponse.airports[currentAirport];
      }
    }
    let selectedFlight = null;
    let selectedFlightFilter: MyTripsFilter | null = null;
    const excludeCorporateTrips: boolean = yield select(
      shouldExcludeCorporateTripsSelector
    );
    const matchesTripId = (flight: BookedFlightItineraryWithDepartureTime) =>
      flight.bookedItinerary.id === tripId &&
      (!excludeCorporateTrips || !getIsCorporateForFlight(flight));
    selectedFlight = [...future, ...present].find(matchesTripId);
    if (!selectedFlight) {
      const canceledFlight = canceled.find(matchesTripId);
      if (canceledFlight) {
        selectedFlight = canceledFlight;
        selectedFlightFilter = MyTripsFilter.CANCELED_TRIPS;
      } else {
        const pastFlight = past.find(matchesTripId);
        if (pastFlight) {
          selectedFlight = pastFlight;
          selectedFlightFilter = MyTripsFilter.PAST_TRIPS;
        }
      }
    }

    if (selectedFlightFilter) {
      if (tripsFilter !== MyTripsFilter.TRAVEL_CREDITS) {
        yield put(actions.setTripsFilter(selectedFlightFilter));
      }
      yield put(actions.populateTripQueryParams(action.history, { tripId }));
    }
    if (selectedFlight) {
      yield put(actions.setSelectedFlight(selectedFlight));
    }

    if (action.includePostBookingOffers) {
      try {
        const offersResponse: AncillaryPostBookingMyTripsFetchOffersResponse =
          yield getPostBookingMyTripsOffers({
            flightItineraryResponseFuture: {
              ...flightItinerariesResponse,
              itineraries: {
                future: flightItinerariesResponse.itineraries.future,
              },
            },
            referenceDateTime: action.request.referenceDateTime,
          });
        yield put(actions.setMyTripsPostBookingOffers(offersResponse.offers));
      } catch (e) {
        yield put(actions.setMyTripsPostBookingOffers([]));
      }
    }

    yield put(
      actions.setFlights(
        {
          ...flightItineriaries,
          present,
          canceled,
          past,
          future,
        },
        action.request.states,
        airports,
        airlines
      )
    );
  } catch (e) {
    yield put(actions.fetchFlightsFailed(action.request));
    console.error(e);
  }
}

const mergeFlightItineraryArrays = (
  array1: BookedFlightItineraryWithDepartureTime[],
  array2: BookedFlightItineraryWithDepartureTime[]
) => {
  const FlightItinerarySet = new Set();
  return array2
    .map((FlightItinerary) => {
      FlightItinerarySet.add(FlightItinerary.bookedItinerary.id);
      return FlightItinerary;
    })
    .concat(
      array1.filter(
        (FlightItinerary) =>
          !FlightItinerarySet.has(FlightItinerary.bookedItinerary.id)
      )
    );
};

// note: array1 has higher priority over array2
const getNewFlightItineraryArray = (
  array1: BookedFlightItineraryWithDepartureTime[] | undefined,
  array2: BookedFlightItineraryWithDepartureTime[]
) => mergeFlightItineraryArrays(array1 ?? [], array2);
